import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"
import { i18n } from "../lib/i18n"

/**
 * Auto-submits the plan filter form via fetch when any input changes.
 * Uses fetch instead of Turbo form submission to avoid triggering
 * turbo:submit-end, which the dynamic-modal controller interprets
 * as a signal to close the modal.
 *
 * The server responds with turbo streams that update only the results
 * and sticky footer — the filter bar DOM (including <details> open state)
 * is intentionally left untouched.
 *
 * Connects to data-controller="plan-filter-form"
 */
export default class extends Controller<HTMLFormElement> {
  private timeout: ReturnType<typeof setTimeout> | undefined

  static values = {
    debounce: { type: Number, default: 300 },
  }

  declare readonly debounceValue: number

  disconnect() {
    clearTimeout(this.timeout)
  }

  submit(event?: Event) {
    // Prevent the browser/Turbo from handling the form natively
    if (event) event.preventDefault()

    clearTimeout(this.timeout)

    this.timeout = setTimeout(() => {
      this.performFetch()
    }, this.debounceValue)
  }

  resetFilters(event: Event) {
    const url = (event.currentTarget as HTMLElement).dataset.planFilterFormUrl!
    this.performPost(url)
  }

  // Picking the "score" match mode switches the sort select to match-score
  // ranking — without it the results stay alphabetical and show no scores.
  // One-way: leaving score mode keeps whatever sort the user has, since
  // score-sort is valid in strict/lenient too.
  syncSort(event: Event) {
    const radio = event.target as HTMLInputElement
    if (radio.value !== "score" || !radio.checked) return

    const sortSelect = this.element.querySelector<HTMLSelectElement>(
      'select[name="placements_plan[matching_filters][sort]"]'
    )
    if (sortSelect && sortSelect.value !== "score") {
      sortSelect.value = "score"
    }
  }

  private async performFetch() {
    const form = this.element
    const formData = new FormData(form)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

    try {
      const response = await fetch(form.action, {
        method: "PATCH",
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken || "",
        },
        body: formData,
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        this.updateFilterBadge()
      }
    } catch (error) {
      console.error("Filter update failed:", error)
    }
  }

  private async performPost(url: string) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken || "",
        },
      })
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        this.updateFilterBadge()
      }
    } catch (error) {
      console.error("Filter action failed:", error)
    }
  }

  // Update the active survey filter count badge in the <details> summary
  // without touching the DOM (keeps the panel open/closed state intact).
  private updateFilterBadge() {
    const badge = document.getElementById("plan_specific_needs_badge")
    if (!badge) return

    const inputs = this.element.querySelectorAll<HTMLInputElement>(
      "[name*='survey_response_filters']"
    )

    // Count unique question keys that have a meaningful value set.
    // Only inputs whose final segment is a true value field count — modifier
    // segments (type, op, match) are filter shape declarations, not values.
    const VALUE_SEGMENTS = new Set(["value", "values", "min", "max"])
    const activeKeys = new Set<string>()
    inputs.forEach(input => {
      const match = input.name.match(/\[survey_response_filters\]\[([^\]]+)\]\[([^\]]+)\]/)
      if (!match) return

      const questionKey = match[1]
      const segment = match[2]
      if (!VALUE_SEGMENTS.has(segment)) return

      // For checkboxes / radios: only count when checked
      if (input.type === "checkbox" || input.type === "radio") {
        if (input.checked && input.value && input.value !== "any") {
          activeKeys.add(questionKey)
        }
        return
      }

      if (input.value && input.value !== "any") {
        activeKeys.add(questionKey)
      }
    })

    const count = activeKeys.size
    ;(badge as HTMLElement).style.display = count === 0 ? "none" : ""
    badge.textContent = count > 0
      ? i18n.t("plan_filter_form.filters_count", { count })
      : ""
  }
}
